import React from "react";
import { useTranslation } from "react-i18next";
import { Pencil, Trash2 } from "lucide-react";

import { Card } from "../../../components/ui/card";
import { useAuth } from "../../../app/providers/AuthProvider";
import { hasPermission, Permission } from "../../../app/permissions";

function ProductImage({ src, alt }) {
  return (
    <div className="aspect-[4/5] w-full overflow-hidden bg-offwhite">
      {src ? (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
        />
      ) : (
        <div className="h-full w-full bg-offwhite" />
      )}
    </div>
  );
}

function AdminIcon({ icon: Icon, testId, label, tone, onClick }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      aria-label={label}
      title={label}
      data-testid={testId}
      className={`inline-flex h-8 w-8 items-center justify-center rounded-md bg-surface/95 backdrop-blur transition-all opacity-0 group-hover:opacity-100 focus-visible:opacity-100 shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-champagne ${
        tone === "danger"
          ? "text-status-unavailable hover:bg-status-unavailable/10"
          : "text-text-primary hover:bg-offwhite"
      }`}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

export function ProductCard({ product, onOpen, onEdit, onDelete }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canManage = hasPermission(user, Permission.ADMIN_PRODUCTS_MANAGE);
  const primaryImage =
    (Array.isArray(product.image_urls) && product.image_urls[0]) ||
    product.image_url ||
    null;

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={() => onOpen(product)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(product);
        }
      }}
      data-testid={`product-card-${product.id}`}
      className="group relative cursor-pointer overflow-hidden rounded-lg border border-border-subtle bg-surface p-0 shadow-card transition-all hover:-translate-y-0.5 hover:shadow-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-champagne"
    >
      {canManage && (
        <div
          className="absolute right-2 top-2 z-10 flex gap-1.5"
          data-testid={`product-card-admin-controls-${product.id}`}
        >
          <AdminIcon
            icon={Pencil}
            label={t("common.edit")}
            testId={`product-card-edit-${product.id}`}
            onClick={() => onEdit && onEdit(product)}
          />
          <AdminIcon
            icon={Trash2}
            label={t("common.delete")}
            testId={`product-card-delete-${product.id}`}
            tone="danger"
            onClick={() => onDelete && onDelete(product)}
          />
        </div>
      )}
      <ProductImage src={primaryImage} alt={product.name} />
      <div className="space-y-1 p-4">
        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-taupe">
          {product.brand || t("products.brand_none")}
        </div>
        <h3
          className="text-base font-semibold leading-snug text-text-primary"
          data-testid={`product-card-name-${product.id}`}
        >
          {product.name}
        </h3>
        <div className="flex items-center justify-between pt-2 text-xs text-text-secondary">
          <span
            className="rounded-full border border-border-subtle bg-offwhite px-2 py-0.5"
            data-testid={`product-card-category-${product.id}`}
          >
            {product.category || t("products.category_none")}
          </span>
          <span
            className="font-medium text-text-primary"
            data-testid={`product-card-stock-${product.id}`}
          >
            {t("products.in_stock_units", { count: product.in_stock ?? 0 })}
          </span>
        </div>
      </div>
    </Card>
  );
}

export function ProductCardSkeleton() {
  return (
    <div className="animate-pulse overflow-hidden rounded-lg border border-border-subtle bg-surface">
      <div className="aspect-[4/5] w-full bg-offwhite" />
      <div className="space-y-2 p-4">
        <div className="h-3 w-16 rounded bg-offwhite" />
        <div className="h-4 w-3/4 rounded bg-offwhite" />
        <div className="h-3 w-1/2 rounded bg-offwhite" />
      </div>
    </div>
  );
}
